import {forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react'
import {useChatbotContext} from '../../context'
import {MessageItem} from '../molecules'
import ChatbotService from '../../services/request'
import InfiniteScroll from 'react-infinite-scroll-component'
import {DEFAULT_PAGE} from '../../../../../_gori/constants'
import useCancelToken from '../../../../../_gori/hooks/UseCancelToken'
import {ChatbotInput} from './ChatbotInput'
import {MessagesSkeleton} from '../atoms/MessagesSkeleton'
import {
  MESSAGE_CONTAINER_ID,
  MESSAGE_TYPE,
  MessageContent,
  MessageModel,
  MessageResponse,
  MessageType,
  SENDER_TYPE,
} from '../../definitions'
import {PaginateModel} from '../../../../../_gori/partials/widgets/paginate/core/_models'
import {first, isEmpty} from 'lodash'
import {useIntl} from 'react-intl'
import {formatTimeAgo} from '../../utils'
import {TIME_FIVE_MINUTES, TIME_ONE_MINUTE} from '../../definitions/time'

const DEFAULT_PAGE_SIZE = 20

const TIMER_COUNT_FIRST_MESSAGE = TIME_ONE_MINUTE

const TIME_SHOW_ACTION_MESSAGE = TIME_FIVE_MINUTES

type MessageData = {
  pagination: PaginateModel | null
  hasLoadMore: boolean
  conversation?: MessageModel<MessageContent>[]
}

export type MessageListForwardRef = {
  getMessages: () => Promise<void>
}

export const MessageList = forwardRef<MessageListForwardRef>((_, ref) => {
  const {messages, setMessages, addMessage} = useChatbotContext()
  const {newCancelToken, isCancel} = useCancelToken()
  const [timeAgo, setTimeAgo] = useState<string | null>(null)
  const intl = useIntl()

  const isSurveyMessage = useCallback((type?: MessageType) => {
    return type === MESSAGE_TYPE.SURVEY
  }, [])

  useEffect(() => {
    if (!messages.length) return

    const firstMessage = first(messages)
    const responseMessages = messages.filter((msg) => !isSurveyMessage(msg.type))
    const firstResponseMessage = first(responseMessages)
    if (
      !firstResponseMessage ||
      firstResponseMessage?.sender !== SENDER_TYPE.ASSISTANT ||
      firstResponseMessage?.type === MESSAGE_TYPE.OPTION_SELECT
    ) {
      setTimeAgo('')
      return
    }

    const countLastTimeSent = async () => {
      const diff =
        Date.now() - new Date(firstResponseMessage.created_at.replace(' ', 'T') + 'Z').getTime()
      if (diff >= TIMER_COUNT_FIRST_MESSAGE) {
        setTimeAgo(
          diff >= TIMER_COUNT_FIRST_MESSAGE
            ? formatTimeAgo(firstResponseMessage.created_at, intl)
            : ''
        )
      } else {
        setTimeAgo('')
      }
      if (diff >= TIME_SHOW_ACTION_MESSAGE && !isSurveyMessage(firstMessage?.type)) {
        const surveyMessage = await ChatbotService.checkMessage(firstResponseMessage.id)
        if (isEmpty(surveyMessage) || !surveyMessage) return

        addMessage({
          id: surveyMessage.id,
          content: surveyMessage.content?.join(' '),
          sender: SENDER_TYPE.ASSISTANT,
          type: surveyMessage.type,
          created_at: surveyMessage.created_at,
        })
      }
    }

    const intervalId = setInterval(countLastTimeSent, TIMER_COUNT_FIRST_MESSAGE)
    countLastTimeSent()

    return () => clearInterval(intervalId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intl, isSurveyMessage, messages])

  const [messageData, setMessageData] = useState<MessageData>({
    pagination: null,
    hasLoadMore: false,
    conversation: [],
  })
  const pageCurrent = useRef(Number(DEFAULT_PAGE))

  useEffect(() => {
    getMessages()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useImperativeHandle(ref, () => ({
    getMessages,
  }))

  const getMessages = async () => {
    pageCurrent.current = Number(DEFAULT_PAGE)

    const response: MessageResponse = await ChatbotService.getMessages({
      params: {
        page_size: DEFAULT_PAGE_SIZE,
      },
      cancelToken: newCancelToken(),
    })
    if (response) {
      const pagination = response?.[0]?.pagination
      setMessageData({
        conversation: response?.[0]?.conversation || [],
        pagination,
        hasLoadMore: (pagination?.total_pages || 1) > pageCurrent.current,
      })
      setMessages(response?.[0]?.conversation || [])
    }
  }

  const getMoreMessages = async () => {
    if (pageCurrent.current >= (messageData?.pagination?.total_pages || 1)) {
      setMessageData((prev) => ({...prev, hasLoadMore: false}))
      return
    }

    pageCurrent.current++

    try {
      const config = {
        params: {page: pageCurrent.current, page_size: DEFAULT_PAGE_SIZE},
        cancelToken: newCancelToken(),
      }
      const response: MessageResponse = await ChatbotService.getMessages(config)
      if (response) {
        const pagination = response?.[0]?.pagination || null

        setMessageData((prev: MessageData) => ({
          conversation: [...(prev.conversation || []), ...(response?.[0]?.conversation || [])],
          pagination,
          hasLoadMore: (response?.[0]?.pagination?.total_pages || 1) > pageCurrent.current,
        }))

        setMessages((prev) => [...prev, ...(response?.[0]?.conversation || [])])
      }
    } catch (error) {
      if (isCancel(error)) return
    }
  }
  return (
    <>
      <div
        id={MESSAGE_CONTAINER_ID}
        // NOTE: The style below is used to reverse the scroll direction
        style={{
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column-reverse',
          paddingTop: 12,
        }}
      >
        <InfiniteScroll
          dataLength={messages.length}
          next={getMoreMessages}
          inverse={true}
          hasMore={messageData?.hasLoadMore}
          loader={<MessagesSkeleton />}
          scrollableTarget={MESSAGE_CONTAINER_ID}
          // NOTE: The style below is used to reverse the scroll direction
          style={{overflow: 'unset', display: 'flex', flexDirection: 'column-reverse'}}
        >
          {messages?.map((message) => (
            <div key={message.id} className='d-flex flex-column gap-3 mb-2 mx-4'>
              <MessageItem message={message} />
              {message.sender === SENDER_TYPE.ASSISTANT &&
                message.id === first(messages)?.id &&
                !!timeAgo && (
                  <span
                    className='chatbot-message-bottom-info fs-8 text-gray-500 ms-4'
                    style={{
                      marginTop: isSurveyMessage(message.type) ? '-30px' : '-10px',
                      alignSelf: 'flex-start',
                    }}
                  >
                    {timeAgo}
                  </span>
                )}
            </div>
          ))}
        </InfiniteScroll>
      </div>
      <ChatbotInput />
    </>
  )
})
